// WP6 Task 8 — the store, rendered once for the panel (spec §10). Figures use
// THE canonical buildFaqFigures call, so a repricing in constants.ts updates
// in-app help on the next deploy with zero copy edits.
import {
  FAQ_ENTRIES,
  buildFaqFigures,
  renderFaqAnswer,
  faqForSurface,
  PLANS,
  TRACKING_PLAN,
  LOCATION_ADDON,
  SEARCH_TERM_ADDON,
  AI_OVERAGE_PACK,
  CONCIERGE,
  SUPPORT_EMAIL,
  DEMO_TTL_DAYS,
  MONEY_BACK_GUARANTEE_DAYS,
  type ScreenId,
} from '@pageboss/shared';

const FIGURES = buildFaqFigures({
  plans: PLANS,
  units: TRACKING_PLAN,
  locationAddon: LOCATION_ADDON,
  searchTermAddon: SEARCH_TERM_ADDON,
  aiPack: AI_OVERAGE_PACK,
  concierge: CONCIERGE,
  supportEmail: SUPPORT_EMAIL,
  demoTtlDays: DEMO_TTL_DAYS,
  moneyBackDays: MONEY_BACK_GUARANTEE_DAYS,
});

export interface HelpEntry {
  id: string;
  question: string;
  answer: string;
  screens: readonly string[];
}

function render(entries: readonly (typeof FAQ_ENTRIES)[number][]): HelpEntry[] {
  return entries.map((e) => ({
    id: e.id,
    question: e.question,
    answer: renderFaqAnswer(e, FIGURES),
    screens: e.screens ?? [],
  }));
}

/** The app_help surface, rendered. Store order = display priority. */
export const HELP_ENTRIES: readonly HelpEntry[] = render(
  faqForSurface([...FAQ_ENTRIES], 'app_help'),
);

/** Search corpus: EVERY store entry (all are customer-facing on /faq). */
const ALL_RENDERED: readonly HelpEntry[] = render([...FAQ_ENTRIES]);

/** The 3-5 entries for the current screen (spec §10). Unknown screen → []. */
export function helpForScreen(screenId: ScreenId | null, max = 5): HelpEntry[] {
  if (!screenId) return [];
  return HELP_ENTRIES.filter((e) => e.screens.includes(screenId)).slice(0, max);
}

/** Case-insensitive substring search over question + rendered answer. */
export function searchHelp(query: string, max = 8): HelpEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return ALL_RENDERED.filter(
    (e) =>
      e.question.toLowerCase().includes(q) || e.answer.toLowerCase().includes(q),
  ).slice(0, max);
}
